import React, { useState, useEffect, useRef } from 'react'
import DashboardLayout from '@/components/DashboardLayout'
import GlassCard from '@/components/ui/GlassCard'
import { getTemplatesByPlan, getTemplateById, templateOptions } from '@/components/templates'
import { agentService } from '@/services/agentService'
import { authService } from '@/services/authService'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { Eye, Save, Crown, Lock, Key, Palette, ExternalLink, X, Globe } from 'lucide-react'
import LanguageSwitcher from '@/components/LanguageSwitcher'

// ─────────────────────────────────────────────────────────────
// Color de acento por template — usado como preview de fondo
// ─────────────────────────────────────────────────────────────
const TEMPLATE_ACCENT: Record<string, string> = {
  showcase:    '#3b82f6',
  elegante:    '#9ca3af',
  moderno:     '#8b5cf6',
  confiable:   '#f59e0b',
  dinamico:    '#ef4444',
  minimalista: '#1f2937',
  natural:     '#22c55e',
  corporativo: '#1e40af',
  orbe:        '#d4a017',
  'orbe-agencia': '#d4a017',
}

// Orden jerárquico de planes para calcular badge dinámico
const PLAN_ORDER = ['free', 'starter', 'smart', 'pro', 'elite']

const PLAN_LABELS: Record<string, { text: string; color: string; border: string; textColor: string }> = {
  free:    { text: 'Free',    color: '#E6F1FB', border: '#85B7EB', textColor: '#0C447C' },
  starter: { text: 'Starter', color: '#EAF3DE', border: '#97C459', textColor: '#27500A' },
  smart:   { text: 'Smart',   color: '#EEEDFE', border: '#AFA9EC', textColor: '#3C3489' },
  pro:     { text: 'Pro',     color: '#FAEEDA', border: '#EF9F27', textColor: '#633806' },
  elite:   { text: 'Elite',   color: '#FAEEDA', border: '#BA7517', textColor: '#412402' },
}

const PLAN_BADGE_CURRENT: Record<string, { text: string; bgClass: string }> = {
  free:     { text: 'Plan Free',     bgClass: 'bg-gray-500/20 text-gray-300 border-gray-500/30' },
  starter:  { text: 'Plan Starter',  bgClass: 'bg-green-500/20 text-green-300 border-green-500/30' },
  smart:    { text: 'Plan Smart',    bgClass: 'bg-purple-500/20 text-purple-300 border-purple-500/30' },
  trialing: { text: 'Trial',         bgClass: 'bg-gray-500/20 text-gray-300 border-gray-500/30' },
  pro:      { text: 'Plan Pro',      bgClass: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
  elite:    { text: 'Plan Elite',    bgClass: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' },
}

// Todos los templates públicos en orden de presentación
const ALL_PUBLIC_TEMPLATE_IDS = [
  'showcase', 'elegante', 'moderno', 'confiable',
  'dinamico', 'minimalista', 'natural', 'corporativo'
]

/**
 * Calcula el plan mínimo requerido para un template
 * leyendo planLimits vía los IDs de templates disponibles por plan.
 * Esta función reemplaza el requiredPlan hardcodeado — fuente única: index.ts templates_available
 */
const getMinPlanForTemplate = (
  templateId: string,
  availableByPlan: Record<string, string[]>
): string | null => {
  for (const plan of PLAN_ORDER) {
    if (availableByPlan[plan]?.includes(templateId)) return plan
  }
  return null
}

const Settings: React.FC = () => {
  const { agent } = useAuth()
  const { t, i18n } = useTranslation('settings')
  const [selectedTemplate, setSelectedTemplate] = useState('elegante')
  const [currentPlan, setCurrentPlan] = useState('free')
  const [loading, setLoading] = useState(false)
  const [saved, setSaved] = useState(false)
  const [activeTab, setActiveTab] = useState<'personalidad' | 'seguridad' | 'idioma'>('personalidad')
  const [expandedCard, setExpandedCard] = useState<string | null>(null)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [passwordData, setPasswordData] = useState({
    currentPassword: '', newPassword: '', confirmPassword: ''
  })
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({})
  const [passwordLoading, setPasswordLoading] = useState(false)

  // Templates disponibles por plan — pre-calculado para badge dinámico
  const [availableByPlan] = useState<Record<string, string[]>>(() => {
    const result: Record<string, string[]> = {}
    for (const plan of PLAN_ORDER) {
      result[plan] = getTemplatesByPlan(plan).map(t => t.id)
    }
    return result
  })

  useEffect(() => {
    const load = async () => {
      try {
        const profile = await agentService.getProfile()
        setSelectedTemplate(profile.template || 'elegante')
        setCurrentPlan(profile.plan || 'free')
      } catch (e) {
        console.error('Error loading profile:', e)
      }
    }
    load()
  }, [])

  const normalizedPlan = currentPlan === 'trialing' ? 'free' : currentPlan
  const availableTemplateIds = getTemplatesByPlan(
    normalizedPlan, agent?.subdomain, undefined
  ).map(t => t.id)

  const planBadge = PLAN_BADGE_CURRENT[currentPlan] || PLAN_BADGE_CURRENT['free']

  const handleSaveTemplate = async () => {
    setLoading(true)
    try {
      await agentService.updateProfile({ template: selectedTemplate })
      setSaved(true)
      toast.success(t('templateSaved'))
      setTimeout(() => setSaved(false), 3000)
    } catch (e) {
      toast.error(t('errorTemplate'))
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setPasswordData(prev => ({ ...prev, [name]: value }))
    if (passwordErrors[name]) {
      setPasswordErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const validatePassword = () => {
    const errors: Record<string, string> = {}
    if (!passwordData.currentPassword) errors.currentPassword = t('required')
    if (!passwordData.newPassword) errors.newPassword = t('required')
    else if (passwordData.newPassword.length < 8) errors.newPassword = t('minChars')
    if (!passwordData.confirmPassword) errors.confirmPassword = t('required')
    else if (passwordData.newPassword !== passwordData.confirmPassword) errors.confirmPassword = t('noMatch')
    setPasswordErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validatePassword()) return
    setPasswordLoading(true)
    try {
      await authService.changePassword(passwordData.currentPassword, passwordData.newPassword)
      toast.success(t('passwordUpdated'))
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' })
      setShowPasswordModal(false)
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('errorPassword'))
    } finally {
      setPasswordLoading(false)
    }
  }

  return (
    <DashboardLayout>
      <div className="space-y-4">

        {/* ── Header ── */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-white">{t('title')}</h1>
            <p className="text-white/60 text-sm">{t('subtitle')}</p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 rounded-full border text-xs font-medium ${planBadge.bgClass}`}>
              {planBadge.text}
            </span>
            {agent?.subdomain && (
              <button
                onClick={() => window.open(`https://${agent.subdomain}.innovagent.tech`, '_blank')}
                className="flex items-center gap-2 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 rounded-lg text-blue-300 text-sm font-medium transition-colors"
              >
                <Eye className="w-4 h-4" />
                {t('viewPublicPage')}
                <ExternalLink className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>

        {/* ── Pill Tabs ── */}
        <div className="flex gap-2 p-1 bg-white/5 rounded-full w-fit border border-white/10">
          {(['personalidad', 'seguridad', 'idioma'] as const).map(tab => {
            const tabConfig = {
              personalidad: { icon: Palette, label: t('tabPersonality') },
              seguridad:    { icon: Key,     label: t('tabSecurity') },
              idioma:       { icon: Globe,   label: t('common:language') }
            }
            const { icon: TabIcon, label } = tabConfig[tab]
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  activeTab === tab
                    ? 'bg-white/15 text-white border border-white/20'
                    : 'text-white/50 hover:text-white/70'
                }`}
              >
                <span className="flex items-center gap-2"><TabIcon className="w-4 h-4" />{label}</span>
              </button>
            )
          })}
        </div>

        {/* ══════════════════════════════════
            TAB: PERSONALIDAD — TEMPLATES
        ══════════════════════════════════ */}
        {activeTab === 'personalidad' && (
          <GlassCard className="p-5">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-lg font-bold text-white">{t('choosePersonality')}</h2>
              <span className="text-white/50 text-xs">
                {t('activeTemplate')} <span className="text-white font-medium">{getTemplateById(selectedTemplate).name}</span>
              </span>
            </div>
            <p className="text-white/50 text-sm mb-5">
              {t('personalityDesc')}
            </p>

            {/* Grid de templates */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-5">
              {ALL_PUBLIC_TEMPLATE_IDS.map(templateId => {
                const template = getTemplateById(templateId)
                const isAvailable = availableTemplateIds.includes(templateId)
                const isSelected = selectedTemplate === templateId
                const isExpanded = expandedCard === templateId
                const accent = TEMPLATE_ACCENT[templateId] || '#667eea'
                const minPlan = getMinPlanForTemplate(templateId, availableByPlan)
                const planLabel = minPlan ? PLAN_LABELS[minPlan] : null

                return (
                  <div
                    key={templateId}
                    onClick={() => {
                      if (isAvailable) {
                        setExpandedCard(isExpanded ? null : templateId)
                        setSelectedTemplate(templateId)
                      }
                    }}
                    className={`relative rounded-xl border transition-all duration-200 overflow-hidden
                      ${isAvailable
                        ? isSelected
                          ? 'border-blue-400 shadow-lg shadow-blue-500/20 cursor-pointer'
                          : 'border-white/10 hover:border-white/30 cursor-pointer'
                        : 'border-white/5 opacity-60 cursor-not-allowed'
                      }
                    `}
                    style={{ minHeight: '90px' }}
                  >
                    {/* Preview background */}
                    <div
                      className="absolute inset-0"
                      style={{ background: accent, opacity: isSelected ? 0.18 : 0.08 }}
                    />

                    {/* Checkmark si seleccionado */}
                    {isSelected && isAvailable && (
                      <div className="absolute top-2 right-2 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center z-10">
                        <span className="text-white text-xs font-bold">✓</span>
                      </div>
                    )}

                    {/* Lock si bloqueado */}
                    {!isAvailable && (
                      <div className="absolute top-2 right-2 z-10">
                        <Lock className="w-3.5 h-3.5 text-white/40" />
                      </div>
                    )}

                    <div className="relative z-10 p-3">
                      {/* Color swatch */}
                      <div
                        className="w-6 h-1.5 rounded-full mb-2"
                        style={{ background: accent }}
                      />

                      <div className="text-white font-medium text-sm">{template.name}</div>

                      {/* Badge plan requerido — solo en bloqueados */}
                      {!isAvailable && planLabel && (
                        <div
                          className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full text-xs font-medium"
                          style={{
                            background: planLabel.color,
                            border: `0.5px solid ${planLabel.border}`,
                            color: planLabel.textColor
                          }}
                        >
                          <Crown style={{ width: 10, height: 10 }} />
                          {planLabel.text}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Panel de detalles expandido — aparece al seleccionar */}
            {expandedCard && (
              <div className="border border-white/10 rounded-xl p-4 bg-white/5 mb-5 relative">
                <button
                  onClick={() => setExpandedCard(null)}
                  className="absolute top-3 right-3 text-white/40 hover:text-white/70"
                >
                  <X className="w-4 h-4" />
                </button>
                {(() => {
                  const t = getTemplateById(expandedCard)
                  const accent = TEMPLATE_ACCENT[expandedCard] || '#667eea'
                  return (
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div
                        className="w-full sm:w-24 h-16 sm:h-auto rounded-lg flex-shrink-0"
                        style={{ background: accent, opacity: 0.3, minHeight: '60px' }}
                      />
                      <div className="flex-1">
                        <h3 className="text-white font-semibold mb-1">{t.name}</h3>
                        <p className="text-white/60 text-sm mb-2">{t.personality}</p>
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {t.emotions.map(e => (
                            <span key={e} className="px-2 py-0.5 bg-white/10 rounded-full text-white/70 text-xs">{e}</span>
                          ))}
                        </div>
                        <p className="text-white/40 text-xs">{t.colorScheme}</p>
                      </div>
                    </div>
                  )
                })()}
              </div>
            )}

            {/* Botón guardar */}
            <div className="flex justify-end">
              <button
                onClick={handleSaveTemplate}
                disabled={loading}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-medium transition-all ${
                  saved
                    ? 'bg-green-500 text-white'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                } disabled:opacity-50`}
              >
                <Save className="w-4 h-4" />
                {saved ? t('saved') : loading ? t('saving') : t('saveTemplate')}
              </button>
            </div>
          </GlassCard>
        )}

        {/* ══════════════════════════════════
            TAB: SEGURIDAD
        ══════════════════════════════════ */}
        {activeTab === 'seguridad' && (
          <GlassCard className="p-5">
            <div className="flex items-center gap-3 mb-1">
              <Key className="w-5 h-5 text-amber-400" />
              <h2 className="text-lg font-bold text-white">{t('securityTitle')}</h2>
            </div>
            <p className="text-white/50 text-sm mb-6">
              {t('securityDesc')}
            </p>

            <button
              onClick={() => setShowPasswordModal(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/30 rounded-xl text-amber-300 font-medium transition-colors"
            >
              <Lock className="w-4 h-4" />
              {t('changePassword')}
            </button>
          </GlassCard>
        )}
        {/* ══════════════════════════════════
            TAB: IDIOMA
        ══════════════════════════════════ */}
        {activeTab === 'idioma' && (
          <GlassCard className="p-5">
            <div className="flex items-center gap-3 mb-1">
              <Globe className="w-5 h-5 text-blue-400" />
              <h2 className="text-lg font-bold text-white">{t('common:language')}</h2>
            </div>
            <p className="text-white/50 text-sm mb-6">
              {t('common:languageDescription')}
            </p>
            <LanguageSwitcher variant="full" />
          </GlassCard>
        )}  
      </div>

      {/* ══════════════════════════════════
          MODAL: CAMBIAR CONTRASEÑA
      ══════════════════════════════════ */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <GlassCard className="p-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-bold text-white">{t('changePassword')}</h3>
                <button
                  onClick={() => {
                    setShowPasswordModal(false)
                    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' })
                    setPasswordErrors({})
                  }}
                  className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-white/60" />
                </button>
              </div>

              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                {[
                  { name: 'currentPassword', label: t('currentPassword'), placeholder: '••••••••' },
                  { name: 'newPassword',     label: t('newPassword'),  placeholder: t('newPasswordPlaceholder') },
                  { name: 'confirmPassword', label: t('confirmPassword'),   placeholder: t('confirmPlaceholder') },
                ].map(field => (
                  <div key={field.name}>
                    <label className="block text-sm text-white/70 mb-1.5">{field.label}</label>
                    <input
                      type="password"
                      name={field.name}
                      value={passwordData[field.name as keyof typeof passwordData]}
                      onChange={handlePasswordChange}
                      placeholder={field.placeholder}
                      className={`w-full px-4 py-2.5 bg-white/10 border rounded-lg text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        passwordErrors[field.name] ? 'border-red-500/60' : 'border-white/20'
                      }`}
                    />
                    {passwordErrors[field.name] && (
                      <p className="text-red-400 text-xs mt-1">{passwordErrors[field.name]}</p>
                    )}
                  </div>
                ))}

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordModal(false)
                      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' })
                      setPasswordErrors({})
                    }}
                    className="flex-1 px-4 py-2.5 bg-white/10 hover:bg-white/20 rounded-xl text-white font-medium transition-colors"
                  >
                    {t('cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={passwordLoading}
                    className="flex-1 px-4 py-2.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 rounded-xl text-white font-medium transition-colors"
                  >
                    {passwordLoading ? t('saving') : t('save')}
                  </button>
                </div>
              </form>
            </GlassCard>
          </div>
        </div>
      )}

    </DashboardLayout>
  )
}

export default Settings
